import { Check } from 'lucide-react';
import {
  MultiSelectClearButton,
  MultiSelectTrigger,
  summarizeSelection,
} from '@/components/custom/multi-select-shell';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: readonly MultiSelectOption[];
  /** Currently-selected values. An empty array conventionally means
   *  "no filter" — the trigger shows `allLabel`. */
  selected: readonly string[];
  onChange: (next: string[]) => void;
  /** Trigger label when nothing is selected (i.e. "all"). */
  allLabel?: string;
  className?: string;
  id?: string;
}

/**
 * Checkbox-style multi-select in a popover. Generic over `{ value, label }`
 * options; the caller owns the selected set (bookmarkable URL state, form
 * field, …). Built on the shadcn Popover primitive — no edits to
 * components/ui (G9). Shares its trigger + Clear footer with the async
 * `EntityMultiSelect` via `multi-select-shell`.
 */
export function MultiSelect({
  options,
  selected,
  onChange,
  allLabel = 'All',
  className,
  id,
}: MultiSelectProps) {
  const selectedSet = new Set(selected);
  const toggle = (value: string) => {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    // Preserve option order for a stable, comparable array.
    onChange(options.map((o) => o.value).filter((v) => next.has(v)));
  };

  const fewLabels = options.filter((o) => selectedSet.has(o.value)).map((o) => o.label);
  const summary = summarizeSelection(selected.length, fewLabels, allLabel);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <MultiSelectTrigger id={id} summary={summary} className={className} />
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        <ul className="max-h-72 space-y-0.5 overflow-auto">
          {options.map((opt) => {
            const isSelected = selectedSet.has(opt.value);
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                  onClick={() => toggle(opt.value)}
                  aria-pressed={isSelected}
                >
                  <span
                    className={cn(
                      'flex size-4 items-center justify-center rounded-sm border',
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input',
                    )}
                  >
                    {isSelected ? <Check className="size-3" /> : null}
                  </span>
                  <span className="flex-1 truncate">{opt.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
        {selected.length > 0 ? (
          <div className="mt-1">
            <MultiSelectClearButton label="Clear" onClear={() => onChange([])} />
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
